import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { api, loadSession, selectClub } from '../state/auth.js';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import SeedDataView from './SeedDataView.jsx';
import { parseServerDate } from '../utils/dates.js';

// ── helpers ───────────────────────────────────────────────────────────────────

// The plans of config/club-types.js; an unknown (old) name counts as Basis there too
const TIER_COLORS = {
  basis:        { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
  plus:         { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  professional: { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
  sportcomplex: { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
};

const TIER_LABELS = { basis: 'Basis', plus: 'Plus', professional: 'Professional', sportcomplex: 'Sportcomplex' };
const TIER_RANK = { basis: 0, plus: 1, professional: 2 };

const dayLabel = day => new Date(`${day}T00:00:00Z`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function TierBadge({ tier }) {
  const c = TIER_COLORS[tier] || TIER_COLORS.basis;
  return (
    <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${c.bg};color:${c.text};border:1px solid ${c.border}`}>
      {TIER_LABELS[tier] || tier}
    </span>
  );
}

function StatusDot({ active }) {
  return (
    <span style={`display:inline-block;width:8px;height:8px;border-radius:50%;background:${active ? 'var(--color-success,#16a34a)' : '#9ca3af'};flex-shrink:0`} />
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({ value, label, onClick, accent }) {
  return (
    <div
      class="stat-card"
      style={`cursor:${onClick ? 'pointer' : 'default'};${accent ? 'border-left:3px solid var(--color-primary)' : ''}`}
      onClick={onClick}
    >
      <div class="stat-card-value">{value}</div>
      <div class="stat-card-label">{label}</div>
    </div>
  );
}

// ── ClubDetail panel ──────────────────────────────────────────────────────────

// Toegang voor ondersteuning: make yourself an admin of the club to help it, and take it
// away again afterwards. The club's admins are told both times (routes/admin.js).
const SUPPORT_ERRORS = {
  club_inactive: 'Het abonnement van deze club is beëindigd. Gebruik de export.',
  not_support_access: 'Dit is een gewoon lidmaatschap, geen toegang voor ondersteuning.',
  no_admin_role: 'Deze club heeft geen rol met beheerrechten (instellingen wijzigen).',
};
function SupportAccessCard({ clubId, access, active, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmOff, setConfirmOff] = useState(false);
  const run = async (method) => {
    setBusy(true); setError('');
    try {
      await api(`/api/admin/clubs/${clubId}/support-access`, { method });
      await loadSession(); // the club selector shows the club (or no longer)
      setConfirmOff(false);
      onChanged();
    } catch (err) {
      setError(SUPPORT_ERRORS[err.code] || translateError(err.code));
    } finally {
      setBusy(false);
    }
  };
  const open = async () => {
    try {
      await selectClub(clubId);
      window.location.assign('/dashboard');
    } catch (err) {
      setError(translateError(err.code));
    }
  };
  const since = access?.since ? parseServerDate(access.since).toLocaleDateString('nl-NL') : '—';
  return (
    <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
      <div style="font-size:13px;font-weight:600;margin-bottom:4px">Toegang voor ondersteuning</div>
      {access?.member && !access.support ? (
        <>
          <div style="font-size:13px;margin-bottom:8px">Je bent gewoon lid van deze club (sinds {since}), niet via ondersteuning. Dat lidmaatschap regelt de club zelf.</div>
          <Button size="sm" onClick={open} disabled={busy || !active}>Naar de club</Button>
        </>
      ) : access?.member ? (
        <>
          <div style="font-size:13px;margin-bottom:8px">Je hebt toegang als beheerder sinds {since}. Haal hem weg als je klaar bent; open werk op jouw naam komt dan zonder eigenaar te staan en de beheerders horen het.</div>
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <Button size="sm" onClick={open} disabled={busy || !active}>Naar de club</Button>
            {confirmOff
              ? <Button size="sm" variant="danger" onClick={() => run('DELETE')} disabled={busy}>Ja, intrekken</Button>
              : <Button size="sm" variant="secondary" onClick={() => setConfirmOff(true)} disabled={busy}>Toegang intrekken</Button>}
          </div>
        </>
      ) : (
        <>
          <div style="font-size:13px;color:var(--color-text-muted);margin-bottom:8px">Word beheerder van deze club om te helpen bij de inrichting, alleen op verzoek van de club. De beheerders krijgen een melding; jij telt niet mee voor hun gebruikerslimiet.</div>
          <Button size="sm" onClick={() => run('POST')} disabled={busy || !active}>Toegang nemen</Button>
        </>
      )}
      {error && <div class="alert alert-error" style="margin-top:8px">{error}</div>}
    </div>
  );
}

// Club wissen na het einde (services/club-erasure.js): when, and 30 days later if needed
const longDay = day => day ? new Date(`${day}T12:00:00Z`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const ERASE_ERRORS = { erase_latest_reached: 'Later kan niet: de verwerkersovereenkomst belooft wissen binnen 90 dagen na het einde. Zet de club weer aan als ze verder wil.' };
function ErasureCard({ clubId, detail, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const postpone = async () => {
    setBusy(true); setError('');
    try { await api(`/api/admin/clubs/${clubId}/erase-postpone`, { method: 'POST' }); onChanged(); }
    catch (err) { setError(ERASE_ERRORS[err.code] || translateError(err.code)); }
    finally { setBusy(false); }
  };
  return (
    <div style="background:var(--color-bg-secondary);border:1px solid var(--color-danger,#dc2626);border-radius:8px;padding:12px">
      <div style="font-size:13px;font-weight:600;margin-bottom:4px">Wordt gewist op {longDay(detail.erase_on)}</div>
      <div style="font-size:13px;color:var(--color-text-muted);margin-bottom:8px">
        Abonnement beëindigd op {longDay(detail.ended_at)}. Op de wisdatum verwijdert de app alles van deze club, ook de accounts van leden zonder andere club.
        {detail.erase_reminder_at ? ' De beheerders hebben de herinnering gehad.' : ' Een week vooraf krijgen de beheerders een herinnering.'}
        {detail.erase_failed_at ? ' Let op: een eerdere poging is mislukt; elk uur volgt een nieuwe.' : ''}
        {' '}Wil de club verder, zet hem dan weer aan. Een export kan tot de wisdatum.
      </div>
      <Button size="sm" variant="secondary" onClick={postpone} disabled={busy}>30 dagen uitstellen</Button>
      {error && <div class="alert alert-error" style="margin-top:8px">{error}</div>}
    </div>
  );
}

// The proof: one line per erased club (no personal data), for a written confirmation
function ErasuresTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api('/api/admin/club-erasures').then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <div style="padding:40px;text-align:center;color:var(--color-text-muted)">{t('common.loading')}</div>;
  return (
    <div class="card" style="padding:16px">
      <p class="text-sm text-muted" style="margin:0 0 12px">Clubs die na het einde van hun abonnement zijn gewist. Hiermee kun je op verzoek schriftelijk bevestigen dat de gegevens zijn verwijderd (verwerkersovereenkomst artikel 12). De beheerders kregen op de wisdag al een bevestiging per mail.</p>
      {rows.length === 0 ? <div class="text-sm text-muted">Nog geen club gewist.</div> : (
        <div style="overflow-x:auto">
          <table class="erasure-log" style="width:100%;font-size:13px;border-collapse:collapse">
            <thead><tr style="text-align:left;color:var(--color-text-muted)"><th>Club</th><th>Einde</th><th>Gewist</th><th>Door</th><th style="text-align:right">Rijen</th><th style="text-align:right">Bestanden</th><th style="text-align:right">Accounts</th></tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} style="border-top:1px solid var(--color-border)">
                <td>{r.club_name}</td><td>{longDay(r.ended_at)}</td><td>{parseServerDate(r.erased_at).toLocaleDateString('nl-NL')}</td>
                <td>{r.erased_by ? 'Platformbeheer' : 'Automatisch'}</td><td style="text-align:right">{r.rows_deleted}</td><td style="text-align:right">{r.files_deleted}</td><td style="text-align:right">{r.accounts_deleted}</td>
              </tr>
            ))}</tbody>
          </table>
          <style>{'.erasure-log th, .erasure-log td { padding: 6px 10px; white-space: nowrap; }'}</style>
        </div>
      )}
    </div>
  );
}

function ClubDetailPanel({ clubId, tiers, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tier, setTier] = useState('');
  // A smaller plan: right away, or from a date (the admins hear it now and a week before)
  const [when, setWhen] = useState('date');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [tierError, setTierError] = useState('');
  const [endOn, setEndOn] = useState('');
  const [endError, setEndError] = useState('');

  const load = () => api(`/api/admin/clubs/${clubId}`)
    .then(d => { setDetail(d); setTier(d.subscription_tier || 'basis'); });

  useEffect(() => {
    if (!clubId) { setDetail(null); return; }
    setLoading(true);
    setTierError('');
    setWhen('date');
    setEffectiveOn('');
    setEndOn('');
    setEndError('');
    load().catch(() => setDetail(null)).finally(() => setLoading(false));
  }, [clubId]);

  const isDown = detail && (TIER_RANK[tier] ?? 0) < (TIER_RANK[detail.subscription_tier] ?? 0);
  const planned = isDown && when === 'date';

  const handleTierSave = async () => {
    setSaving(true);
    setTierError('');
    try {
      if (planned) {
        await api(`/api/admin/clubs/${clubId}/plan-schedule`, { method: 'POST', body: JSON.stringify({ to_tier: tier, effective_on: effectiveOn }) });
      } else {
        await api(`/api/admin/clubs/${clubId}`, { method: 'PUT', body: JSON.stringify({ subscription_tier: tier }) });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await load();
      onUpdated();
    } catch (err) {
      setTierError(translateError(err.code, err.detail));
    }
    setSaving(false);
  };

  // The end of the subscription from a date: the admins hear it now and a week before
  const handleScheduleEnd = async () => {
    setEndError('');
    try {
      await api(`/api/admin/clubs/${clubId}/plan-schedule`, { method: 'POST', body: JSON.stringify({ kind: 'cancel', effective_on: endOn }) });
      setEndOn('');
      await load();
    } catch (err) {
      setEndError(translateError(err.code, err.detail));
    }
  };

  const handleWithdraw = async () => {
    setTierError('');
    try {
      await api(`/api/admin/clubs/${clubId}/plan-schedule`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setTierError(translateError(err.code, err.detail));
    }
  };

  const handleToggleActive = async () => {
    if (!detail) return;
    // Why ask: switching off starts the erase clock (60 days, then everything is gone)
    if (detail.is_active && !window.confirm(`${detail.name} uitzetten? Leden kunnen er dan niet meer bij, en over 60 dagen wist de app alle gegevens van de club. Weer aanzetten stopt dat.`)) return;
    await api(`/api/admin/clubs/${clubId}`, { method: 'PUT', body: JSON.stringify({ is_active: !detail.is_active }) });
    onUpdated();
    const fresh = await api(`/api/admin/clubs/${clubId}`);
    setDetail(fresh);
  };

  if (!clubId) return (
    <div style="text-align:center;padding:80px 20px;color:var(--color-text-muted)">
      <div style="font-size:32px;margin-bottom:12px">🏢</div>
      <p>{t('platform.noClubSelected')}</p>
    </div>
  );

  if (loading) return <div style="padding:40px;text-align:center;color:var(--color-text-muted)">{t('common.loading')}</div>;
  if (!detail) return null;

  const totalProcesses = detail.processCounts?.reduce((s, r) => s + r.cnt, 0) || 0;
  const activeProcesses = detail.processCounts?.find(r => r.status === 'actief')?.cnt || 0;
  const tierDef = tiers.find(t => t.value === tier);

  return (
    <div style="display:flex;flex-direction:column;gap:16px;padding:4px">
      {/* Club header */}
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <StatusDot active={detail.is_active} />
        <h2 style="font-size:18px;font-weight:700;margin:0;flex:1">{detail.name}</h2>
        <TierBadge tier={detail.subscription_tier} />
      </div>

      <div style="font-size:12px;color:var(--color-text-muted)">{detail.type} · {detail.slug}</div>

      {/* Quick stats */}
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px">
        {[
          { val: detail.members?.length || 0, label: t('platform.members') },
          { val: detail.roles?.length || 0, label: t('platform.allRoles') },
          { val: detail.templates?.length || 0, label: t('platform.templates') },
        ].map(({ val, label }) => (
          <div key={label} style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:10px;text-align:center">
            <div style="font-size:22px;font-weight:700;color:var(--color-primary)">{val}</div>
            <div style="font-size:11px;color:var(--color-text-muted)">{label}</div>
          </div>
        ))}
      </div>

      {!detail.is_active && detail.erase_on && (
        <ErasureCard clubId={clubId} detail={detail} onChanged={async () => setDetail(await api(`/api/admin/clubs/${clubId}`))} />
      )}

      <SupportAccessCard clubId={clubId} access={detail.supportAccess} active={!!detail.is_active}
        onChanged={async () => { setDetail(await api(`/api/admin/clubs/${clubId}`)); onUpdated(); }} />

      {/* Processes */}
      {detail.processCounts?.length > 0 && (
        <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:8px">{t('platform.processCounts')}</div>
          {detail.processCounts.map(r => (
            <div key={r.status} style="display:flex;justify-content:space-between;font-size:13px;padding:2px 0">
              <span style="color:var(--color-text-muted)">{r.status}</span>
              <span style="font-weight:600">{r.cnt}</span>
            </div>
          ))}
        </div>
      )}

      {/* A sportcomplex has its own plan in 4U-beheer, not a club plan */}
      {detail.kind === 'sportcomplex' && (
        <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px;font-size:13px;color:var(--color-text-muted)">
          Sportcomplex: het pakket en de prijs lopen via 4U-beheer. De clubs sluiten zich zelf aan vanuit hun instellingen.
        </div>
      )}

      {/* Abonnement wijzigen */}
      {detail.kind !== 'sportcomplex' && <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
        <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:10px">{t('platform.changeTier')}</div>
        {detail.planSchedule?.kind === 'downgrade' && (
          <div style="border:1px solid #fcd34d;background:#fffbeb;border-radius:6px;padding:10px;margin-bottom:10px;font-size:13px">
            <div style="font-weight:600;margin-bottom:4px">Gepland: {detail.planSchedule.toLabel} vanaf {dayLabel(detail.planSchedule.effectiveOn)}</div>
            {detail.planSchedule.lost?.length > 0 && <div style="color:var(--color-text-muted)">Gaat op slot: {detail.planSchedule.lost.join(', ')}</div>}
            {detail.planSchedule.overLimit && <div style="color:var(--color-text-muted)">{detail.planSchedule.overLimit.members} actieve leden, {detail.planSchedule.toLabel} staat er {detail.planSchedule.overLimit.max} toe</div>}
            <div style="margin-top:8px"><Button variant="secondary" size="sm" onClick={handleWithdraw}>Intrekken</Button></div>
          </div>
        )}
        {detail.planSchedule?.kind === 'cancel' && (
          <div style="border:1px solid #fca5a5;background:#fef2f2;border-radius:6px;padding:10px;margin-bottom:10px;font-size:13px">
            <div style="font-weight:600;margin-bottom:4px">Gepland: abonnement beëindigd vanaf {dayLabel(detail.planSchedule.effectiveOn)}</div>
            <div style="color:var(--color-text-muted)">Vanaf die dag kunnen leden niet meer bij de club; de gegevens blijven bewaard.</div>
            <div style="margin-top:8px"><Button variant="secondary" size="sm" onClick={handleWithdraw}>Intrekken</Button></div>
          </div>
        )}
        <div style="display:flex;flex-direction:column;gap:6px;margin-bottom:10px">
          {tiers.map(tr => (
            <label key={tr.value} style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:8px;border-radius:6px;border:1px solid;border-color:${tier === tr.value ? 'var(--color-primary)' : 'var(--color-border)'};background:${tier === tr.value ? 'var(--color-primary-light,#eff6ff)' : 'transparent'}">
              <input
                type="radio"
                name="tier"
                value={tr.value}
                checked={tier === tr.value}
                onChange={() => setTier(tr.value)}
                style="accent-color:var(--color-primary)"
              />
              <div style="flex:1">
                <div style="font-size:13px;font-weight:600">{tr.label}</div>
                <div style="font-size:11px;color:var(--color-text-muted)">
                  {tr.maxUsers ? `Max. ${tr.maxUsers} gebruikers` : 'Onbeperkt gebruikers'}
                </div>
              </div>
            </label>
          ))}
        </div>
        {isDown && (
          <div style="display:flex;flex-direction:column;gap:6px;margin-bottom:10px;font-size:13px">
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
              <input type="radio" name="when" checked={when === 'date'} onChange={() => setWhen('date')} style="accent-color:var(--color-primary)" />
              Vanaf een datum (beheerders krijgen nu en een week vooraf bericht)
            </label>
            {when === 'date' && (
              <input class="form-input" type="date" min={tomorrow()} value={effectiveOn} onInput={e => setEffectiveOn(e.target.value)} style="margin-left:24px;max-width:180px" />
            )}
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
              <input type="radio" name="when" checked={when === 'now'} onChange={() => setWhen('now')} style="accent-color:var(--color-primary)" />
              Direct (beheerders krijgen achteraf bericht)
            </label>
          </div>
        )}
        {tierError && <div class="alert alert-error mb-2" style="font-size:13px">{tierError}</div>}
        <div style="display:flex;align-items:center;gap:10px">
          <Button
            variant="primary"
            size="sm"
            onClick={handleTierSave}
            disabled={saving || tier === detail.subscription_tier || (planned && !effectiveOn)}
          >
            {saving ? t('common.loading') : planned ? 'Plannen' : t('common.save')}
          </Button>
          {saved && <span style="font-size:12px;color:var(--color-success,#16a34a)">✓ {t('platform.tierSaved')}</span>}
        </div>
      </div>}

      {/* Rollen */}
      {detail.roles?.length > 0 && (
        <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:8px">{t('platform.allRoles')}</div>
          <div style="display:flex;flex-wrap:wrap;gap:4px">
            {detail.roles.map(r => (
              <span key={r.id} style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px">{r.name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Leden */}
      {detail.members?.length > 0 && (
        <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:8px">{t('platform.members')}</div>
          {detail.members.map(m => (
            <div key={m.id} style="display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--color-border);font-size:12px">
              <div style="flex:1">
                <span style="font-weight:500">{m.display_name}</span>
                <span style="color:var(--color-text-muted);margin-left:6px">{m.email}</span>
              </div>
              {m.roles && <span style="color:var(--color-text-muted);font-size:11px">{m.roles}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Abonnement beëindigen vanaf een datum */}
      {detail.is_active && !detail.planSchedule && (
        <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:6px">Abonnement beëindigen</div>
          <div style="font-size:13px;color:var(--color-text-muted);margin-bottom:8px">Vanaf de gekozen dag is de club niet meer te openen. Beheerders krijgen nu en een week vooraf bericht; de gegevens blijven bewaard.</div>
          {endError && <div class="alert alert-error mb-2" style="font-size:13px">{endError}</div>}
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
            <input class="form-input" type="date" min={tomorrow()} value={endOn} onInput={e => setEndOn(e.target.value)} style="max-width:180px" />
            <Button variant="danger" size="sm" onClick={handleScheduleEnd} disabled={!endOn}>Beëindiging plannen</Button>
          </div>
        </div>
      )}

      {/* Activeren/deactiveren */}
      <div style="padding-top:4px">
        <Button
          variant={detail.is_active ? 'danger' : 'primary'}
          size="sm"
          onClick={handleToggleActive}
        >
          {detail.is_active ? t('platform.deactivate') : t('platform.activate')} club
        </Button>
      </div>
    </div>
  );
}

// ── UserDetailPanel ───────────────────────────────────────────────────────────
// Note: renders NO modal itself — calls onRequestDelete(detail) so the parent
// can render the modal outside any overflow:hidden/auto container.

function UserDetailPanel({ userId, currentUserId, onRequestDelete }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) { setDetail(null); return; }
    setLoading(true);
    api(`/api/admin/users/${userId}`)
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return (
    <div style="text-align:center;padding:80px 20px;color:var(--color-text-muted)">
      <div style="font-size:32px;margin-bottom:12px">👤</div>
      <p>Selecteer een gebruiker</p>
    </div>
  );

  if (loading) return <div style="padding:40px;text-align:center;color:var(--color-text-muted)">{t('common.loading')}</div>;
  if (!detail) return null;

  const isSelf = detail.id === currentUserId;

  return (
    <div style="display:flex;flex-direction:column;gap:14px">
      {/* Header */}
      <div style="display:flex;align-items:center;gap:10px">
        <div style="width:40px;height:40px;border-radius:50%;background:var(--color-primary);display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:700;flex-shrink:0">
          {(detail.display_name || detail.email)[0].toUpperCase()}
        </div>
        <div style="flex:1;min-width:0">
          <div style="font-size:16px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{detail.display_name}</div>
          <div style="font-size:12px;color:var(--color-text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{detail.email}</div>
        </div>
        {detail.is_platform_admin && (
          <span class="badge badge-actief" style="flex-shrink:0">Platform Admin</span>
        )}
      </div>

      {/* Meta */}
      <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px;font-size:13px;display:flex;flex-direction:column;gap:6px">
        <div style="display:flex;justify-content:space-between">
          <span style="color:var(--color-text-muted)">Aangemaakt</span>
          <span>{detail.created_at ? parseServerDate(detail.created_at).toLocaleDateString('nl-NL') : '—'}</span>
        </div>
        <div style="display:flex;justify-content:space-between">
          <span style="color:var(--color-text-muted)">Open taken</span>
          <span style={`font-weight:600;color:${detail.openTasks > 0 ? 'var(--color-warning,#d97706)' : 'inherit'}`}>{detail.openTasks}</span>
        </div>
        <div style="display:flex;justify-content:space-between">
          <span style="color:var(--color-text-muted)">Actieve processen</span>
          <span style={`font-weight:600;color:${detail.activeProcesses > 0 ? 'var(--color-warning,#d97706)' : 'inherit'}`}>{detail.activeProcesses}</span>
        </div>
      </div>

      {/* Club memberships */}
      {detail.clubs?.length > 0 && (
        <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:12px">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;color:var(--color-text-muted);margin-bottom:8px">
            Clublidmaatschappen ({detail.clubs.length})
          </div>
          <div style="display:flex;flex-direction:column;gap:8px">
            {detail.clubs.map(c => (
              <div key={c.id} style="font-size:13px">
                <div style="display:flex;align-items:center;gap:6px">
                  <span style={`width:7px;height:7px;border-radius:50%;background:${c.membership_active ? 'var(--color-success,#16a34a)' : '#9ca3af'};flex-shrink:0`} />
                  <span style="font-weight:500">{c.name}</span>
                  <span style="font-size:11px;color:var(--color-text-muted)">{c.type}</span>
                </div>
                {c.roles && <div style="font-size:11px;color:var(--color-text-muted);margin-left:13px;margin-top:2px">{c.roles}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete button — calls parent so modal renders outside overflow container */}
      {!isSelf && !detail.is_platform_admin && (
        <div style="padding-top:4px">
          <Button variant="danger" size="sm" onClick={() => onRequestDelete(detail)}>
            Gebruiker permanent verwijderen
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Roles aggregation tab ─────────────────────────────────────────────────────

function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api('/api/admin/roles')
      .then(setRoles)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group by club
  const byClub = roles.reduce((acc, r) => {
    const key = r.club_id;
    if (!acc[key]) acc[key] = { club_id: r.club_id, club_name: r.club_name, club_type: r.club_type, roles: [] };
    acc[key].roles.push(r);
    return acc;
  }, {});

  const clubs = Object.values(byClub).filter(c =>
    !search || c.club_name.toLowerCase().includes(search.toLowerCase()) ||
    c.roles.some(r => r.name.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return <div style="padding:40px;text-align:center;color:var(--color-text-muted)">{t('common.loading')}</div>;

  return (
    <div>
      <div style="margin-bottom:16px">
        <input
          class="form-input"
          type="text"
          placeholder="Zoek op club of rolnaam..."
          value={search}
          onInput={e => setSearch(e.target.value)}
          style="max-width:320px"
        />
      </div>
      {clubs.map(c => (
        <div key={c.club_id} class="card" style="margin-bottom:12px;padding:16px">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
            <span style="font-size:14px;font-weight:700">{c.club_name}</span>
            <span style="font-size:11px;background:var(--color-bg-secondary);border:1px solid var(--color-border);padding:1px 7px;border-radius:10px;color:var(--color-text-muted)">{c.club_type}</span>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:6px">
            {c.roles.map(r => (
              <div key={r.id} style="display:flex;align-items:center;gap:4px;background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:8px;padding:4px 10px">
                <span style="font-size:12px;font-weight:500">{r.name}</span>
                {r.member_count > 0 && (
                  <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:0 5px;border-radius:8px;margin-left:2px">{r.member_count}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {clubs.length === 0 && <div style="text-align:center;padding:40px;color:var(--color-text-muted)">{t('common.noResults')}</div>}
    </div>
  );
}

// ── Updates Tab ───────────────────────────────────────────────────────────────

const UPDATE_TYPES = [
  { value: 'feature',     label: '✨ Nieuwe functie' },
  { value: 'improvement', label: '⚡ Verbetering' },
  { value: 'bugfix',      label: '🐛 Bugfix' },
];

const TYPE_COLORS = {
  feature:     { bg: '#dcfce7', color: '#16a34a' },
  improvement: { bg: '#fef3c7', color: '#d97706' },
  bugfix:      { bg: '#dbeafe', color: '#2563eb' },
};

function UpdatesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', version: '', update_type: 'feature', is_published: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { loadItems(); }, []);

  async function loadItems() {
    setLoading(true);
    try {
      const data = await api('/api/admin/updates');
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }

  function openNew() {
    setEditItem(null);
    setForm({ title: '', content: '', version: '', update_type: 'feature', is_published: true });
    setShowForm(true);
  }

  function openEdit(item) {
    setEditItem(item);
    setForm({ title: item.title, content: item.content, version: item.version || '', update_type: item.update_type, is_published: !!item.is_published });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      if (editItem) {
        const updated = await api(`/api/admin/updates/${editItem.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      } else {
        const created = await api('/api/admin/updates', { method: 'POST', body: JSON.stringify(form) });
        setItems(prev => [created, ...prev]);
      }
      setShowForm(false);
    } catch {}
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!confirm('Update verwijderen?')) return;
    setDeleting(id);
    try {
      await api(`/api/admin/updates/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px">
        <div>
          <div style="font-size:15px;font-weight:600">Platform updates</div>
          <div style="font-size:13px;color:var(--color-text-muted)">Publiceer release notes die alle gebruikers zien via het 🚀 icoon in de header.</div>
        </div>
        {!showForm && <Button onClick={openNew}>+ Nieuwe update</Button>}
      </div>

      {/* Form */}
      {showForm && (
        <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:20px;margin-bottom:20px">
          <h3 style="font-size:15px;font-weight:600;margin:0 0 16px">{editItem ? 'Update bewerken' : 'Nieuwe update'}</h3>
          <div style="display:flex;flex-direction:column;gap:12px">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
              <div>
                <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Type</label>
                <select class="form-input" value={form.update_type} onChange={e => setForm(f => ({ ...f, update_type: e.target.value }))}>
                  {UPDATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Versie (optioneel)</label>
                <input class="form-input" value={form.version} onInput={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="bijv. 1.4.2" />
              </div>
            </div>
            <div>
              <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Titel *</label>
              <input class="form-input" value={form.title} onInput={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Bijv. Club Coins systeem gelanceerd" />
            </div>
            <div>
              <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Beschrijving *</label>
              <textarea class="form-input" style="min-height:100px;resize:vertical" value={form.content} onInput={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Beschrijf de wijziging..." />
            </div>
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px">
              <input type="checkbox" checked={form.is_published} onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))} style="accent-color:var(--color-primary)" />
              Direct publiceren (zichtbaar voor alle gebruikers)
            </label>
          </div>
          <div style="display:flex;gap:8px;margin-top:16px">
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !form.title.trim() || !form.content.trim()}>
              {saving ? 'Opslaan...' : 'Publiceren'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowForm(false)}>Annuleren</Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style="display:flex;flex-direction:column;gap:10px">
          {[0,1,2].map(i => <div key={i} class="skeleton" style="height:80px;border-radius:10px" />)}
        </div>
      ) : items.length === 0 ? (
        <div style="text-align:center;padding:60px;color:var(--color-text-muted);border:1px dashed var(--color-border);border-radius:12px">
          <div style="font-size:32px;margin-bottom:8px">🚀</div>
          <p style="font-size:14px;margin:0">Nog geen updates. Maak je eerste release note aan!</p>
        </div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:10px">
          {items.map(item => {
            const meta = TYPE_COLORS[item.update_type] || TYPE_COLORS.feature;
            const typeLabel = UPDATE_TYPES.find(t => t.value === item.update_type)?.label || item.update_type;
            return (
              <div key={item.id} style="display:flex;align-items:flex-start;gap:14px;padding:14px;border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface)">
                <div style="flex:1;min-width:0">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;flex-wrap:wrap">
                    <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${meta.bg};color:${meta.color}`}>{typeLabel}</span>
                    {item.version && <span style="font-size:11px;font-family:monospace;background:var(--color-border);padding:2px 8px;border-radius:10px;color:var(--color-text-muted)">v{item.version}</span>}
                    {!item.is_published && <span style="font-size:11px;background:#f3f4f6;color:#6b7280;padding:2px 8px;border-radius:10px">Concept</span>}
                    <span style="font-size:11px;color:var(--color-text-muted)">{parseServerDate(item.created_at).toLocaleDateString('nl-NL')}</span>
                  </div>
                  <div style="font-weight:600;font-size:14px;margin-bottom:4px">{item.title}</div>
                  <div style="font-size:13px;color:var(--color-text-muted);overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical">{item.content}</div>
                </div>
                <div style="display:flex;gap:6px;flex-shrink:0">
                  <Button variant="secondary" size="sm" onClick={() => openEdit(item)}>Bewerken</Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)} disabled={deleting === item.id}>
                    {deleting === item.id ? '...' : 'Verwijderen'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Jaarplanning Starter Sets Tab ────────────────────────────────────────────

const MONTH_LABELS_STARTER = {
  '01': 'Januari', '02': 'Februari', '03': 'Maart',
  '04': 'April',   '05': 'Mei',      '06': 'Juni',
  '07': 'Juli',    '08': 'Augustus', '09': 'September',
  '10': 'Oktober', '11': 'November', '12': 'December',
  'continue': 'Doorlopend',
};

const ROLE_COLORS_STARTER = {
  O: { bg: '#fef3c7', color: '#92400e' },
  U: { bg: '#dbeafe', color: '#1e40af' },
  a: { bg: '#f0fdf4', color: '#166534' },
};

function StarterSetsTab() {
  const [sets, setSets]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSet, setExpandedSet] = useState(null);

  const [setForm, setSetForm]   = useState(null);
  const [savingSet, setSavingSet] = useState(false);

  // Activity form — includes assignments (array of { entity_name, entity_type, role })
  const [actForm, setActForm]     = useState(null);
  const [savingAct, setSavingAct] = useState(false);

  // Inline assignment editor within actForm
  const [newAssign, setNewAssign] = useState({ entity_name: '', entity_type: 'commission', role: 'U' });

  function load() {
    setLoading(true);
    fetch('/api/admin/starter-sets', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setSets(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function saveSet() {
    setSavingSet(true);
    const method = setForm.id ? 'PUT' : 'POST';
    const url = setForm.id ? `/api/admin/starter-sets/${setForm.id}` : '/api/admin/starter-sets';
    await fetch(url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: setForm.name, description: setForm.description, sport_type: setForm.sport_type }),
    });
    setSavingSet(false);
    setSetForm(null);
    load();
  }

  async function deleteSet(id) {
    if (!confirm('Startersset verwijderen? Alle activiteiten in de set worden ook verwijderd.')) return;
    await fetch(`/api/admin/starter-sets/${id}`, { method: 'DELETE', credentials: 'include' });
    load();
  }

  async function saveAct() {
    setSavingAct(true);
    const method = actForm.id ? 'PUT' : 'POST';
    const url = actForm.id
      ? `/api/admin/starter-sets/${actForm.setId}/activities/${actForm.id}`
      : `/api/admin/starter-sets/${actForm.setId}/activities`;

    const r = await fetch(url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: actForm.name, description: actForm.description, planning_month: actForm.planning_month }),
    });
    const saved = await r.json();
    const resolvedId = actForm.id || saved.id;

    // Save assignments
    await fetch(`/api/admin/starter-sets/${actForm.setId}/activities/${resolvedId}/assignments`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignments: actForm.assignments }),
    });

    setSavingAct(false);
    setActForm(null);
    load();
  }

  async function deleteAct(setId, actId) {
    if (!confirm('Activiteit verwijderen uit de set?')) return;
    await fetch(`/api/admin/starter-sets/${setId}/activities/${actId}`, { method: 'DELETE', credentials: 'include' });
    load();
  }

  function openActForm(setId, act) {
    setActForm({
      setId,
      id: act?.id || null,
      name: act?.name || '',
      description: act?.description || '',
      planning_month: act?.planning_month || '08',
      assignments: act?.assignments ? [...act.assignments] : [],
    });
    setNewAssign({ entity_name: '', entity_type: 'commission', role: 'U' });
  }

  function addAssignment() {
    if (!newAssign.entity_name.trim()) return;
    const key = `${newAssign.entity_type}:${newAssign.entity_name.toLowerCase()}`;
    const exists = actForm.assignments.some(
      a => a.entity_type === newAssign.entity_type && a.entity_name.toLowerCase() === newAssign.entity_name.toLowerCase()
    );
    if (exists) return;
    setActForm(f => ({ ...f, assignments: [...f.assignments, { ...newAssign, entity_name: newAssign.entity_name.trim() }] }));
    setNewAssign(n => ({ ...n, entity_name: '' }));
  }

  function removeAssignment(idx) {
    setActForm(f => ({ ...f, assignments: f.assignments.filter((_, i) => i !== idx) }));
  }

  if (loading) return <div style="padding:24px;color:var(--color-text-muted)">Laden…</div>;

  return (
    <div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
        <p style="margin:0;font-size:13px;color:var(--color-text-muted)">
          Startersets zijn kant-en-klare jaarplanning-activiteiten die clubs kunnen importeren. Toewijzingen worden bij import op naam gematcht aan commissies/secties van de club.
        </p>
        <button
          onClick={() => setSetForm({ id: null, name: '', description: '', sport_type: '' })}
          style="padding:6px 14px;background:var(--color-primary,#2563eb);color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">
          + Nieuwe set
        </button>
      </div>

      {sets.length === 0 && (
        <div class="card" style="padding:32px;text-align:center;color:var(--color-text-muted)">
          Nog geen startersets. Maak er een aan met de knop hierboven.
        </div>
      )}

      {sets.map(s => (
        <div key={s.id} class="card mb-4" style="padding:0;overflow:hidden">
          <div style="display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--color-border);background:var(--color-surface-alt,#f9fafb)">
            <button
              onClick={() => setExpandedSet(expandedSet === s.id ? null : s.id)}
              style="background:none;border:none;cursor:pointer;font-size:14px;color:var(--color-text-muted);padding:0;width:20px">
              {expandedSet === s.id ? '▼' : '▶'}
            </button>
            <div style="flex:1">
              <span style="font-weight:700;font-size:14px">{s.name}</span>
              {s.sport_type && <span style="margin-left:8px;font-size:11px;background:#dbeafe;color:#1e40af;padding:1px 8px;border-radius:10px">{s.sport_type}</span>}
              {s.description && <div style="font-size:12px;color:var(--color-text-muted);margin-top:1px">{s.description}</div>}
            </div>
            <span style="font-size:12px;color:var(--color-text-muted)">{s.activities.length} activiteiten</span>
            <button onClick={() => setSetForm({ id: s.id, name: s.name, description: s.description || '', sport_type: s.sport_type || '' })}
              style="font-size:12px;color:var(--color-text-muted);background:none;border:none;cursor:pointer;padding:2px 8px">✎</button>
            <button onClick={() => deleteSet(s.id)}
              style="font-size:12px;color:#ef4444;background:none;border:none;cursor:pointer;padding:2px 8px">✕</button>
          </div>

          {expandedSet === s.id && (
            <div>
              {s.activities.map(a => (
                <div key={a.id} style="padding:8px 16px 8px 36px;border-bottom:1px solid var(--color-border)">
                  <div style="display:flex;align-items:center;gap:10px">
                    <span style="font-size:11px;background:var(--color-surface-alt,#f3f4f6);color:var(--color-text-muted);padding:1px 7px;border-radius:8px;white-space:nowrap;min-width:70px;text-align:center">
                      {MONTH_LABELS_STARTER[a.planning_month] || a.planning_month}
                    </span>
                    <span style="flex:1;font-size:13px;font-weight:500">{a.name}</span>
                    {a.description && <span style="font-size:11px;color:var(--color-text-muted);max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{a.description}</span>}
                    <button onClick={() => openActForm(s.id, a)}
                      style="font-size:11px;color:var(--color-text-muted);background:none;border:none;cursor:pointer;padding:2px 6px">✎</button>
                    <button onClick={() => deleteAct(s.id, a.id)}
                      style="font-size:11px;color:#ef4444;background:none;border:none;cursor:pointer;padding:2px 6px">✕</button>
                  </div>
                  {/* Assignment chips */}
                  {a.assignments && a.assignments.length > 0 && (
                    <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:5px;padding-left:84px">
                      {a.assignments.map((asgn, i) => {
                        const rc = ROLE_COLORS_STARTER[asgn.role] || {};
                        return (
                          <span key={i} style={`font-size:10px;padding:1px 7px;border-radius:8px;background:${rc.bg};color:${rc.color};display:inline-flex;align-items:center;gap:3px`}>
                            {asgn.entity_type === 'commission' ? '📋' : '🏢'} {asgn.entity_name} <strong>{asgn.role}</strong>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <div style="padding:8px 16px 8px 36px">
                <button
                  onClick={() => openActForm(s.id, null)}
                  style="font-size:12px;color:var(--color-primary,#2563eb);background:none;border:none;cursor:pointer;padding:0">
                  + Activiteit toevoegen
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Set modal */}
      {setForm && (
        <div style="position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:100;display:flex;align-items:center;justify-content:center">
          <div style="background:var(--color-bg);border-radius:10px;padding:24px;min-width:400px;max-width:480px;box-shadow:0 8px 32px rgba(0,0,0,.18)">
            <h3 style="margin:0 0 16px;font-size:16px">{setForm.id ? 'Set bewerken' : 'Nieuwe startersset'}</h3>
            <div style="margin-bottom:12px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Naam *</label>
              <input class="form-input" style="width:100%;box-sizing:border-box" placeholder="Bijv. Voetbalvereniging standaard" value={setForm.name}
                onInput={e => setSetForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style="margin-bottom:12px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Omschrijving</label>
              <input class="form-input" style="width:100%;box-sizing:border-box" placeholder="Optioneel" value={setForm.description}
                onInput={e => setSetForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Sporttype</label>
              <input class="form-input" style="width:100%;box-sizing:border-box" placeholder="Bijv. Voetbal, Tennis, Handbal" value={setForm.sport_type}
                onInput={e => setSetForm(f => ({ ...f, sport_type: e.target.value }))} />
            </div>
            <div style="display:flex;gap:8px;justify-content:flex-end">
              <button onClick={() => setSetForm(null)} style="padding:6px 14px;border:1px solid var(--color-border);border-radius:6px;background:transparent;cursor:pointer;font-size:13px">Annuleren</button>
              <button onClick={saveSet} disabled={savingSet || !setForm.name?.trim()}
                style="padding:6px 14px;background:var(--color-primary,#2563eb);color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">
                {savingSet ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity modal — with assignment editor */}
      {actForm && (
        <div style="position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:100;display:flex;align-items:center;justify-content:center;overflow-y:auto;padding:20px">
          <div style="background:var(--color-bg);border-radius:10px;padding:24px;min-width:440px;max-width:540px;width:100%;box-shadow:0 8px 32px rgba(0,0,0,.18)">
            <h3 style="margin:0 0 16px;font-size:16px">{actForm.id ? 'Activiteit bewerken' : 'Activiteit toevoegen'}</h3>
            <div style="margin-bottom:12px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Naam *</label>
              <input class="form-input" style="width:100%;box-sizing:border-box" placeholder="Bijv. Seizoensopening organiseren" value={actForm.name}
                onInput={e => setActForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style="margin-bottom:12px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Omschrijving</label>
              <input class="form-input" style="width:100%;box-sizing:border-box" placeholder="Optioneel" value={actForm.description}
                onInput={e => setActForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px">Planningsmaand</label>
              <select class="form-input" style="width:100%;box-sizing:border-box" value={actForm.planning_month}
                onChange={e => setActForm(f => ({ ...f, planning_month: e.target.value }))}>
                {['01','02','03','04','05','06','07','08','09','10','11','12','continue'].map(m => (
                  <option key={m} value={m}>{MONTH_LABELS_STARTER[m]}</option>
                ))}
              </select>
            </div>

            {/* Assignments */}
            <div style="margin-bottom:16px">
              <label style="font-size:12px;font-weight:600;display:block;margin-bottom:6px">
                Rolverdeling <span style="font-weight:400;color:var(--color-text-muted)">— op commissienaam (wordt bij import gematcht)</span>
              </label>

              {actForm.assignments.length > 0 && (
                <div style="margin-bottom:8px;display:flex;flex-direction:column;gap:4px">
                  {actForm.assignments.map((a, i) => {
                    const rc = ROLE_COLORS_STARTER[a.role] || {};
                    return (
                      <div key={i} style="display:flex;align-items:center;gap:8px;padding:4px 8px;background:var(--color-surface-alt,#f3f4f6);border-radius:6px">
                        <select value={a.entity_type} onChange={e => setActForm(f => ({ ...f, assignments: f.assignments.map((x, j) => j === i ? { ...x, entity_type: e.target.value } : x) }))}
                          style="font-size:11px;border:1px solid var(--color-border);border-radius:4px;padding:1px 4px;background:var(--color-bg)">
                          <option value="commission">Commissie</option>
                          <option value="section">Sectie</option>
                        </select>
                        <input value={a.entity_name} onInput={e => setActForm(f => ({ ...f, assignments: f.assignments.map((x, j) => j === i ? { ...x, entity_name: e.target.value } : x) }))}
                          style="flex:1;font-size:12px;border:1px solid var(--color-border);border-radius:4px;padding:2px 6px;background:var(--color-bg)" />
                        {['O','U','a'].map(r => (
                          <button key={r} type="button" onClick={() => setActForm(f => ({ ...f, assignments: f.assignments.map((x, j) => j === i ? { ...x, role: r } : x) }))}
                            style={`padding:1px 8px;border-radius:8px;font-size:11px;font-weight:700;cursor:pointer;border:1.5px solid ${a.role === r ? rc.color : 'var(--color-border)'};background:${a.role === r ? rc.bg : 'transparent'};color:${a.role === r ? rc.color : 'var(--color-text-muted)'}`}>
                            {r}
                          </button>
                        ))}
                        <button onClick={() => removeAssignment(i)}
                          style="font-size:11px;color:#ef4444;background:none;border:none;cursor:pointer;padding:0">✕</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new assignment row */}
              <div style="display:flex;gap:6px;align-items:center">
                <select value={newAssign.entity_type} onChange={e => setNewAssign(n => ({ ...n, entity_type: e.target.value }))}
                  style="font-size:11px;border:1px solid var(--color-border);border-radius:4px;padding:3px 4px;background:var(--color-bg)">
                  <option value="commission">Commissie</option>
                  <option value="section">Sectie</option>
                </select>
                <input
                  class="form-input"
                  style="flex:1;font-size:12px"
                  placeholder="Naam commissie / sectie"
                  value={newAssign.entity_name}
                  onInput={e => setNewAssign(n => ({ ...n, entity_name: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAssignment(); } }}
                />
                <select value={newAssign.role} onChange={e => setNewAssign(n => ({ ...n, role: e.target.value }))}
                  style="font-size:11px;border:1px solid var(--color-border);border-radius:4px;padding:3px 4px;background:var(--color-bg)">
                  <option value="O">O – Opdrachtgevend</option>
                  <option value="U">U – Uitvoerend</option>
                  <option value="a">a – Assisterend</option>
                </select>
                <button type="button" onClick={addAssignment} disabled={!newAssign.entity_name.trim()}
                  style="padding:3px 10px;background:var(--color-primary,#2563eb);color:#fff;border:none;border-radius:4px;font-size:12px;cursor:pointer;white-space:nowrap">
                  + Toevoegen
                </button>
              </div>
            </div>

            <div style="display:flex;gap:8px;justify-content:flex-end">
              <button onClick={() => setActForm(null)} style="padding:6px 14px;border:1px solid var(--color-border);border-radius:6px;background:transparent;cursor:pointer;font-size:13px">Annuleren</button>
              <button onClick={saveAct} disabled={savingAct || !actForm.name?.trim()}
                style="padding:6px 14px;background:var(--color-primary,#2563eb);color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer">
                {savingAct ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Help Admin Tab ────────────────────────────────────────────────────────────

const HELP_CATEGORIES = [
  'algemeen','processen','taken','documenten','leden','beheer',
  'sjablonen','rapportages','sportlink','ai','coins','marktplaats',
];

const HELP_PAGE_SLUGS = [
  { val: '', label: '— Alle paginas' },
  { val: 'dashboard', label: 'Dashboard' },
  { val: 'processes', label: 'Processen' },
  { val: 'tasks', label: 'Taken' },
  { val: 'documents', label: 'Documenten' },
  { val: 'templates', label: 'Sjablonen' },
  { val: 'users', label: 'Leden' },
  { val: 'reports', label: 'Rapportages' },
  { val: 'sportlink', label: 'SportLink' },
  { val: 'coins', label: 'Club Coins' },
  { val: 'marketplace', label: 'Marktplaats' },
  { val: 'settings', label: 'Instellingen' },
];

const BLANK_HELP_FORM = { title: '', content: '', category: 'algemeen', article_type: 'handleiding', page_slug: '', order_index: 0, is_published: true };

function HelpAdminTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(BLANK_HELP_FORM);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => { loadArticles(); }, []);

  async function loadArticles() {
    setLoading(true);
    try {
      const data = await api('/api/help/admin/articles');
      setArticles(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }

  function openNew() {
    setEditItem(null);
    setForm(BLANK_HELP_FORM);
    setShowForm(true);
    setPreview(false);
  }

  function openEdit(item) {
    setEditItem(item);
    setForm({
      title: item.title, content: item.content,
      category: item.category, article_type: item.article_type,
      page_slug: item.page_slug || '', order_index: item.order_index || 0,
      is_published: !!item.is_published,
    });
    setShowForm(true);
    setPreview(false);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, page_slug: form.page_slug || null };
      if (editItem) {
        const updated = await api(`/api/help/admin/articles/${editItem.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        setArticles(prev => prev.map(a => a.id === updated.id ? updated : a));
      } else {
        const created = await api('/api/help/admin/articles', { method: 'POST', body: JSON.stringify(payload) });
        setArticles(prev => [...prev, created]);
      }
      setShowForm(false);
    } catch {}
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!confirm('Artikel verwijderen?')) return;
    await api(`/api/help/admin/articles/${id}`, { method: 'DELETE' });
    setArticles(prev => prev.filter(a => a.id !== id));
  }

  async function togglePublish(item) {
    const updated = await api(`/api/help/admin/articles/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...item, page_slug: item.page_slug || null, is_published: !item.is_published }),
    });
    setArticles(prev => prev.map(a => a.id === updated.id ? updated : a));
  }

  const filtered = filterType === 'all' ? articles : articles.filter(a => a.article_type === filterType);

  return (
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;flex-wrap:wrap;gap:12px">
        <div>
          <div style="font-size:15px;font-weight:600">Handleiding & werkinstructies beheren</div>
          <div style="font-size:13px;color:var(--color-text-muted)">Maak artikelen aan die gebruikers kunnen raadplegen via /help of de ? knop.</div>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <select class="form-select" style="height:36px;font-size:13px" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">Alle types</option>
            <option value="handleiding">Handleiding</option>
            <option value="werkinstructie">Werkinstructie</option>
          </select>
          {!showForm && <Button onClick={openNew}>+ Nieuw artikel</Button>}
        </div>
      </div>

      {/* Editor form */}
      {showForm && (
        <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:20px;margin-bottom:20px">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
            <h3 style="font-size:15px;font-weight:600;margin:0">{editItem ? 'Artikel bewerken' : 'Nieuw artikel'}</h3>
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer">
              <input type="checkbox" checked={preview} onChange={e => setPreview(e.target.checked)} style="accent-color:var(--color-primary)" />
              Voorbeeld
            </label>
          </div>

          {preview ? (
            <div style="border:1px solid var(--color-border);border-radius:8px;padding:20px;min-height:200px">
              <h2 style="font-size:18px;font-weight:700;margin:0 0 12px">{form.title || '(geen titel)'}</h2>
              <div style="font-size:13px;color:var(--color-text);white-space:pre-wrap;line-height:1.7">{form.content}</div>
            </div>
          ) : (
            <div style="display:flex;flex-direction:column;gap:12px">
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
                <div>
                  <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Type</label>
                  <select class="form-input" value={form.article_type} onChange={e => setForm(f => ({ ...f, article_type: e.target.value }))}>
                    <option value="handleiding">📖 Handleiding</option>
                    <option value="werkinstructie">📋 Werkinstructie</option>
                  </select>
                </div>
                <div>
                  <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Categorie</label>
                  <select class="form-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {HELP_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div style="display:grid;grid-template-columns:1fr auto auto;gap:12px;align-items:end">
                <div>
                  <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Gekoppelde pagina (contextgevoelige hulp)</label>
                  <select class="form-input" value={form.page_slug} onChange={e => setForm(f => ({ ...f, page_slug: e.target.value }))}>
                    {HELP_PAGE_SLUGS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Volgorde</label>
                  <input class="form-input" type="number" min="0" style="width:70px" value={form.order_index} onInput={e => setForm(f => ({ ...f, order_index: Number(e.target.value) }))} />
                </div>
                <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer;padding-bottom:4px">
                  <input type="checkbox" checked={form.is_published} onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))} style="accent-color:var(--color-primary)" />
                  Publiceren
                </label>
              </div>
              <div>
                <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">Titel *</label>
                <input class="form-input" value={form.title} onInput={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Bijv. Hoe start ik een proces?" />
              </div>
              <div>
                <label style="display:block;font-size:12px;font-weight:500;color:var(--color-text-muted);margin-bottom:4px">
                  Inhoud * <span style="font-weight:400;color:var(--color-text-muted)">(ondersteunt ## Koppen, **vet**, *cursief*, - lijsten)</span>
                </label>
                <textarea
                  class="form-input"
                  style="min-height:260px;resize:vertical;font-family:monospace;font-size:13px;line-height:1.6"
                  value={form.content}
                  onInput={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder={'## Inleiding\n\nSchrijf hier de inhoud van je artikel.\n\n## Stappen\n\n1. Eerste stap\n2. Tweede stap\n\n**Tip:** gebruik markdown voor opmaak'}
                />
              </div>
            </div>
          )}

          <div style="display:flex;gap:8px;margin-top:16px">
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !form.title.trim() || !form.content.trim()}>
              {saving ? 'Opslaan...' : editItem ? 'Bijwerken' : 'Aanmaken'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowForm(false)}>Annuleren</Button>
          </div>
        </div>
      )}

      {/* Article list */}
      {loading ? (
        <div style="display:flex;flex-direction:column;gap:8px">{[0,1,2].map(i => <div key={i} class="skeleton" style="height:60px;border-radius:8px" />)}</div>
      ) : filtered.length === 0 ? (
        <div style="text-align:center;padding:60px;color:var(--color-text-muted);border:1px dashed var(--color-border);border-radius:12px">
          <div style="font-size:32px;margin-bottom:8px">📚</div>
          <p style="font-size:14px;margin:0">Nog geen artikelen. Maak je eerste handleiding aan!</p>
        </div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:6px">
          {filtered.map(item => (
            <div key={item.id} style="display:flex;align-items:center;gap:12px;padding:12px 16px;border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface)">
              <div style="flex:1;min-width:0">
                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:3px">
                  <span style="font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{item.title}</span>
                  <span style={`font-size:10px;font-weight:600;padding:1px 7px;border-radius:8px;${item.article_type === 'werkinstructie' ? 'background:#dcfce7;color:#16a34a' : 'background:#dbeafe;color:#1d4ed8'}`}>
                    {item.article_type === 'werkinstructie' ? 'Werkinstructie' : 'Handleiding'}
                  </span>
                  {!item.is_published && <span style="font-size:10px;background:#f3f4f6;color:#6b7280;padding:1px 7px;border-radius:8px">Concept</span>}
                  {item.page_slug && <span style="font-size:10px;background:#ede9fe;color:#7c3aed;padding:1px 7px;border-radius:8px">/{item.page_slug}</span>}
                </div>
                <div style="font-size:11px;color:var(--color-text-muted)">{item.category} · volgorde {item.order_index}</div>
              </div>
              <div style="display:flex;gap:6px;flex-shrink:0">
                <button
                  onClick={() => togglePublish(item)}
                  style={`padding:4px 10px;border-radius:6px;border:1px solid;font-size:12px;cursor:pointer;${item.is_published ? 'border-color:#d1d5db;background:#f9fafb;color:#6b7280' : 'border-color:var(--color-primary);background:var(--color-primary);color:#fff'}`}
                >
                  {item.is_published ? 'Verbergen' : 'Publiceren'}
                </button>
                <Button variant="secondary" size="sm" onClick={() => openEdit(item)}>Bewerken</Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>×</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────

const FEATURE_LABELS = {
  uses_coins:        { label: 'Club Coins',       icon: '🪙' },
  uses_seasons:      { label: 'Seizoenen',         icon: '📅' },
  uses_recurring:    { label: 'Terugkerende proc.', icon: '🔄' },
  uses_sportlink:    { label: 'SportLink',          icon: '🔗' },
  uses_ai:           { label: 'AI-schrijfhulp',     icon: '✨' },
  marketplace_pub:   { label: 'Marktplaats pub.',   icon: '🛒' },
  marketplace_imp:   { label: 'Marktplaats imp.',   icon: '📥' },
  uses_folders:      { label: 'Documentmappen',     icon: '📁' },
};

function MiniSparkline({ data, color = 'var(--color-primary)', height = 40, width = 200 }) {
  if (!data || data.length === 0) return null;
  const vals = data.map(d => d.value ?? d.count ?? 0);
  const max = Math.max(...vals, 1);
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style="display:block;overflow:visible">
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={pts} />
      <polyline
        fill={color}
        fillOpacity="0.12"
        stroke="none"
        points={`0,${height} ${pts} ${width},${height}`}
      />
    </svg>
  );
}

function FeatureBar({ label, icon, count, total, pct }) {
  const barPct = Math.round(pct || 0);
  return (
    <div style="display:grid;grid-template-columns:130px 1fr 60px;align-items:center;gap:10px;padding:6px 0">
      <div style="font-size:13px;display:flex;align-items:center;gap:5px">
        <span>{icon}</span>
        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{label}</span>
      </div>
      <div style="background:var(--color-border);border-radius:4px;height:10px;overflow:hidden">
        <div style={`height:100%;width:${barPct}%;background:var(--color-primary);border-radius:4px;transition:width .4s ease`} />
      </div>
      <div style="font-size:12px;color:var(--color-text-muted);text-align:right">{count} ({barPct}%)</div>
    </div>
  );
}

function GrowthChart({ data }) {
  if (!data || data.length === 0) return null;
  const W = 500, H = 120, PAD = 30;
  const dates = data.map(d => d.date);
  const clubs = data.map(d => Number(d.new_clubs || 0));
  const users = data.map(d => Number(d.new_users || 0));
  const maxC = Math.max(...clubs, 1);
  const maxU = Math.max(...users, 1);
  const n = data.length;

  function polyline(vals, maxV, color) {
    const pts = vals.map((v, i) => {
      const x = PAD + (i / (n - 1)) * (W - PAD * 2);
      const y = H - PAD - (v / maxV) * (H - PAD * 2);
      return `${x},${y}`;
    }).join(' ');
    return (
      <polyline key={color} fill="none" stroke={color} strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" points={pts} />
    );
  }

  // X-axis labels every ~15 days
  const xLabels = data.filter((_, i) => i === 0 || i === n - 1 || i % 15 === 0);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style="display:block">
      {/* Y gridlines */}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <line key={f}
          x1={PAD} y1={H - PAD - f * (H - PAD * 2)}
          x2={W - PAD} y2={H - PAD - f * (H - PAD * 2)}
          stroke="var(--color-border)" strokeWidth="1" strokeDasharray="3,3" />
      ))}
      {/* Baseline */}
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--color-border)" strokeWidth="1" />
      {polyline(clubs, maxC, 'var(--color-primary)')}
      {polyline(users, maxU, '#f59e0b')}
      {/* X labels */}
      {xLabels.map((d, i) => {
        const idx = data.indexOf(d);
        const x = PAD + (idx / (n - 1)) * (W - PAD * 2);
        return (
          <text key={i} x={x} y={H - 8} textAnchor="middle"
            style="font-size:9px;fill:var(--color-text-muted)">{d.date?.slice(5)}</text>
        );
      })}
    </svg>
  );
}

function AnalyticsTab() {
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [clubRows, setClubRows] = useState([]);
  const [adoption, setAdoption] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('activity_count');
  const [sortDir, setSortDir] = useState('desc');
  const [clubSearch, setClubSearch] = useState('');

  useEffect(() => {
    const endpoints = [
      api('/api/admin/analytics/overview'),
      api('/api/admin/analytics/growth'),
      api('/api/admin/analytics/clubs'),
      api('/api/admin/analytics/feature-adoption'),
      api('/api/admin/analytics/activity-trend'),
    ];
    Promise.all(endpoints.map(p => p.catch(() => null)))
      .then(([ov, gr, cl, ad, tr]) => {
        setOverview(ov || {});
        setGrowth(Array.isArray(gr) ? gr : []);
        setClubRows(Array.isArray(cl) ? cl : []);
        setAdoption(Array.isArray(ad) ? ad : []);
        setTrend(Array.isArray(tr) ? tr : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortedClubs = [...clubRows]
    .filter(c => !clubSearch || c.name.toLowerCase().includes(clubSearch.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const trendData = trend.map(d => ({ value: Number(d.total_activity || 0) }));

  if (loading) return <div style="padding:60px;text-align:center;color:var(--color-text-muted)">Analytics laden...</div>;

  const ov = overview || {};
  const totalClubs = Number(ov.total_clubs || 0);

  return (
    <div style="display:flex;flex-direction:column;gap:24px">

      {/* Overview stat grid */}
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px">
        {[
          { val: ov.active_clubs ?? 0,    label: 'Actieve clubs',      icon: '🏢', color: 'var(--color-primary)' },
          { val: ov.total_users ?? 0,     label: 'Totaal gebruikers',  icon: '👥', color: '#8b5cf6' },
          { val: ov.total_processes ?? 0, label: 'Processen',          icon: '⚙️',  color: '#f59e0b' },
          { val: ov.total_tasks ?? 0,     label: 'Taken',              icon: '✅', color: '#10b981' },
        ].map(({ val, label, icon, color }) => (
          <div key={label} style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:16px">
            <div style={`font-size:28px;font-weight:700;color:${color}`}>{val}</div>
            <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px">{icon} {label}</div>
          </div>
        ))}
      </div>

      {/* Secondary stats */}
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px">
        {[
          { val: ov.total_documents ?? 0,   label: 'Documenten' },
          { val: ov.total_templates ?? 0,   label: 'Sjablonen' },
          { val: ov.marketplace_published ?? 0, label: 'Marktplaats items' },
          { val: ov.total_coins_issued ?? 0, label: 'Coins uitgegeven' },
        ].map(({ val, label }) => (
          <div key={label} style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:10px;padding:12px">
            <div style="font-size:22px;font-weight:700">{val}</div>
            <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px">{label}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">

        {/* Feature adoption */}
        <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:20px">
          <div style="font-size:14px;font-weight:600;margin-bottom:16px">Feature adoptie <span style="font-size:12px;font-weight:400;color:var(--color-text-muted)">({totalClubs} clubs totaal)</span></div>
          {adoption.length === 0 ? (
            <div style="text-align:center;padding:30px;color:var(--color-text-muted);font-size:13px">Geen data</div>
          ) : (
            <div style="display:flex;flex-direction:column;gap:2px">
              {adoption.map(f => {
                const meta = FEATURE_LABELS[f.feature_key] || { label: f.feature_key, icon: '📌' };
                return (
                  <FeatureBar
                    key={f.feature_key}
                    label={meta.label}
                    icon={meta.icon}
                    count={f.count}
                    total={totalClubs}
                    pct={f.pct}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Activity trend */}
        <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:20px">
          <div style="font-size:14px;font-weight:600;margin-bottom:4px">Platform activiteit <span style="font-size:12px;font-weight:400;color:var(--color-text-muted)">(30 dagen)</span></div>
          <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:16px">Totaal acties per dag over alle clubs</div>
          {trendData.length > 0 ? (
            <MiniSparkline data={trendData} height={100} width={380} />
          ) : (
            <div style="text-align:center;padding:40px;color:var(--color-text-muted);font-size:13px">Geen activiteit data</div>
          )}
          {trend.length > 0 && (
            <div style="display:flex;justify-content:space-between;margin-top:8px;font-size:11px;color:var(--color-text-muted)">
              <span>{trend[0]?.date?.slice(5)}</span>
              <span style="font-weight:600;color:var(--color-text)">{trend.reduce((s, d) => s + Number(d.total_activity || 0), 0).toLocaleString('nl-NL')} totaal</span>
              <span>{trend[trend.length - 1]?.date?.slice(5)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Growth chart */}
      <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:20px">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
          <div>
            <div style="font-size:14px;font-weight:600">Groei over tijd <span style="font-size:12px;font-weight:400;color:var(--color-text-muted)">(60 dagen)</span></div>
            <div style="font-size:12px;color:var(--color-text-muted)">Nieuwe clubs en gebruikers per dag</div>
          </div>
          <div style="display:flex;align-items:center;gap:14px;font-size:12px;color:var(--color-text-muted)">
            <span><span style="display:inline-block;width:12px;height:3px;background:var(--color-primary);border-radius:2px;vertical-align:middle;margin-right:4px" />Clubs</span>
            <span><span style="display:inline-block;width:12px;height:3px;background:#f59e0b;border-radius:2px;vertical-align:middle;margin-right:4px" />Gebruikers</span>
          </div>
        </div>
        {growth.length > 0 ? <GrowthChart data={growth} /> : (
          <div style="text-align:center;padding:40px;color:var(--color-text-muted);font-size:13px">Geen groei data beschikbaar</div>
        )}
      </div>

      {/* Per-club table */}
      <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;overflow:hidden">
        <div style="padding:16px 20px;border-bottom:1px solid var(--color-border);display:flex;align-items:center;gap:12px">
          <div style="font-size:14px;font-weight:600;flex:1">Club overzicht</div>
          <input
            class="form-input"
            type="text"
            placeholder="Zoek club..."
            value={clubSearch}
            onInput={e => setClubSearch(e.target.value)}
            style="max-width:200px;height:32px;font-size:13px"
          />
        </div>
        <div style="overflow-x:auto">
          <table style="width:100%;border-collapse:collapse;min-width:700px">
            <thead>
              <tr style="border-bottom:1px solid var(--color-border);background:var(--color-bg-secondary)">
                {[
                  { key: 'name',           label: 'Club' },
                  { key: 'member_count',   label: 'Leden' },
                  { key: 'process_count',  label: 'Processen' },
                  { key: 'task_count',     label: 'Taken' },
                  { key: 'doc_count',      label: 'Docs' },
                  { key: 'activity_count', label: 'Activiteit (30d)' },
                  { key: null,             label: 'Features' },
                ].map(col => (
                  <th
                    key={col.label}
                    style={`padding:10px 14px;text-align:left;font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;${col.key ? 'cursor:pointer;user-select:none' : ''}`}
                    onClick={col.key ? () => toggleSort(col.key) : undefined}
                  >
                    {col.label}
                    {col.key && sortKey === col.key && <span style="margin-left:4px">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedClubs.map(club => (
                <tr key={club.id} style="border-bottom:1px solid var(--color-border)">
                  <td style="padding:10px 14px">
                    <div style="font-size:13px;font-weight:500">{club.name}</div>
                    <div style="font-size:11px;color:var(--color-text-muted)">{club.type}</div>
                  </td>
                  <td style="padding:10px 14px;font-size:13px;text-align:center">{club.member_count ?? 0}</td>
                  <td style="padding:10px 14px;font-size:13px;text-align:center">{club.process_count ?? 0}</td>
                  <td style="padding:10px 14px;font-size:13px;text-align:center">{club.task_count ?? 0}</td>
                  <td style="padding:10px 14px;font-size:13px;text-align:center">{club.doc_count ?? 0}</td>
                  <td style="padding:10px 14px;font-size:13px;text-align:center">
                    <span style={`font-weight:600;color:${Number(club.activity_count) > 0 ? 'var(--color-primary)' : 'var(--color-text-muted)'}`}>
                      {club.activity_count ?? 0}
                    </span>
                  </td>
                  <td style="padding:10px 14px">
                    <div style="display:flex;gap:3px;flex-wrap:wrap">
                      {Object.entries(FEATURE_LABELS).map(([key, { icon }]) =>
                        club[key] ? (
                          <span key={key} title={FEATURE_LABELS[key].label}
                            style="font-size:14px;opacity:0.9" aria-label={FEATURE_LABELS[key].label}>
                            {icon}
                          </span>
                        ) : null
                      )}
                      {!Object.keys(FEATURE_LABELS).some(k => club[k]) && (
                        <span style="font-size:11px;color:var(--color-text-muted)">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {sortedClubs.length === 0 && (
                <tr>
                  <td colSpan={7} style="padding:40px;text-align:center;color:var(--color-text-muted);font-size:13px">Geen clubs gevonden</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function PlatformAdminView() {
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('clubs');
  const [clubs, setClubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [clubTypes, setClubTypes] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [locales, setLocales] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', type: 'voetbal', language: 'nl', tier: '', adminName: '', adminEmail: '' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // currentUserId needed so UserDetailPanel can hide "delete self" button
  const [currentUserId, setCurrentUserId] = useState(null);
  useEffect(() => {
    import('../state/auth.js').then(({ authState }) => {
      setCurrentUserId(authState.value.userId);
    });
  }, []);

  // Delete user modal — lives here (not in the panel) so it's outside overflow:auto
  const [deleteTarget, setDeleteTarget] = useState(null); // full user detail object
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await api(`/api/admin/users/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      setSelectedUserId(null);
      fetchAll();
    } catch (err) {
      setDeleteError(
        err.code === 'cannot_delete_platform_admin' ? 'Platform-admins kunnen niet worden verwijderd.' :
        err.code === 'cannot_delete_self' ? 'Je kunt jezelf niet verwijderen.' :
        'Er is een fout opgetreden.'
      );
    } finally {
      setDeleting(false);
    }
  };

  const fetchAll = () => {
    api('/api/admin/stats').then(setStats).catch(() => {});
    api('/api/admin/clubs').then(setClubs).catch(() => {});
    api('/api/admin/users').then(setUsers).catch(() => {});
  };

  useEffect(() => {
    fetchAll();
    api('/api/admin/club-types').then(setClubTypes).catch(() => {});
    api('/api/admin/subscription-tiers').then(setTiers).catch(() => {});
    api('/api/auth/locales').then(setLocales).catch(() => {});
  }, []);

  const createClub = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    try {
      const data = await api('/api/admin/clubs', { method: 'POST', body: JSON.stringify(form) });
      setResult(data);
      fetchAll();
    } catch (err) {
      setError(translateError(err.code, err.detail));
    }
  };

  const selectedType = clubTypes.find(ct => ct.value === form.type);

  const tabs = [
    { key: 'clubs',       label: t('platform.clubs') },
    { key: 'users',       label: t('platform.allUsers') },
    { key: 'roles',       label: t('platform.allRoles') },
    { key: 'seeds',       label: t('platform.seeds.title') },
    { key: 'starter-sets', label: '🗓 Startersets' },
    { key: 'updates',     label: '🚀 Updates' },
    { key: 'analytics',   label: '📊 Analytics' },
    { key: 'help',        label: '📚 Handleiding' },
    { key: 'erasures',    label: '🗑 Gewiste clubs' },
  ];

  return (
    <div>
      <h1 class="page-title mb-6">{t('platform.title')}</h1>

      {/* Stat tiles — klikbaar */}
      <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:24px">
        <StatCard value={stats.totalClubs ?? 0} label={t('platform.stats.totalClubs')} accent onClick={() => setActiveTab('clubs')} />
        <StatCard value={stats.totalUsers ?? 0} label={t('platform.stats.totalUsers')} onClick={() => setActiveTab('users')} />
        <StatCard value={stats.totalProcesses ?? 0} label={t('platform.stats.totalProcesses')} />
        <StatCard value={stats.totalTasks ?? 0} label={t('platform.stats.totalTasks')} />
        <StatCard value={stats.totalDocuments ?? 0} label={t('platform.stats.totalDocuments')} />
      </div>

      {/* Tabs + new club button */}
      <div class="flex justify-between items-center mb-4">
        <div class="tabs" style="margin:0;border:none">
          {tabs.map(tab => (
            <button
              key={tab.key}
              class={`tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'clubs' && (
          <Button onClick={() => { setShowNew(true); setResult(null); setError(''); }}>
            {t('platform.newClub')}
          </Button>
        )}
      </div>

      {/* Tab content */}
      {activeTab === 'help' ? (
        <HelpAdminTab />
      ) : activeTab === 'analytics' ? (
        <AnalyticsTab />
      ) : activeTab === 'seeds' ? (
        <SeedDataView />
      ) : activeTab === 'erasures' ? (
        <ErasuresTab />
      ) : activeTab === 'starter-sets' ? (
        <StarterSetsTab />
      ) : activeTab === 'updates' ? (
        <UpdatesTab />
      ) : activeTab === 'roles' ? (
        <RolesTab />
      ) : activeTab === 'users' ? (
        /* Users tab — two-panel layout */
        <>
          <div style="display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:16px;align-items:start">
            {/* User list */}
            <div class="card" style="padding:0;overflow:hidden;min-width:0">
              <div style="padding:12px 14px;border-bottom:1px solid var(--color-border)">
                <input
                  class="form-input"
                  type="text"
                  placeholder="Zoek op naam of e-mail…"
                  value={userSearch}
                  onInput={e => setUserSearch(e.target.value)}
                  style="max-width:300px"
                />
              </div>
              <table style="width:100%;border-collapse:collapse;table-layout:fixed">
                <colgroup>
                  <col style="width:22%" />
                  <col style="width:38%" />
                  <col style="width:25%" />
                  <col style="width:15%" />
                </colgroup>
                <thead>
                  <tr style="border-bottom:2px solid var(--color-border)">
                    {[t('user.name'), t('user.email'), t('platform.clubs'), t('common.type')].map(h => (
                      <th key={h} style="padding:10px 14px;text-align:left;font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users
                    .filter(u => !userSearch || u.display_name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()))
                    .map(u => (
                    <tr
                      key={u.id}
                      style={`border-bottom:1px solid var(--color-border);cursor:pointer;background:${selectedUserId === u.id ? 'var(--color-primary-light,#eff6ff)' : 'transparent'}`}
                      onClick={() => setSelectedUserId(u.id === selectedUserId ? null : u.id)}
                    >
                      <td style="padding:10px 14px;font-size:14px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{u.display_name}</td>
                      <td style="padding:10px 14px;font-size:13px;color:var(--color-text-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{u.email}</td>
                      <td style="padding:10px 14px;font-size:12px;color:var(--color-text-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{u.clubs || '—'}</td>
                      <td style="padding:10px 14px">
                        {u.is_platform_admin
                          ? <span class="badge badge-actief">Platform Admin</span>
                          : <span class="badge badge-inactive">{t('user.title')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* User detail panel — no overflow:auto so modal can escape */}
            <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:10px;padding:16px;position:sticky;top:80px">
              <UserDetailPanel
                userId={selectedUserId}
                currentUserId={currentUserId}
                onRequestDelete={detail => { setDeleteError(''); setDeleteTarget(detail); }}
              />
            </div>
          </div>

          {/* Delete modal — rendered at this level, outside the sticky panel */}
          <Modal
            isOpen={!!deleteTarget}
            title="Gebruiker permanent verwijderen"
            onClose={() => !deleting && setDeleteTarget(null)}
          >
            <p style="color:var(--color-text-muted);line-height:1.6;margin-bottom:16px">
              Je staat op het punt <strong>{deleteTarget?.display_name}</strong> ({deleteTarget?.email}) volledig uit het systeem te verwijderen.
              Dit verwijdert het account uit <strong>alle clubs</strong> en kan niet ongedaan worden gemaakt.
            </p>
            {(deleteTarget?.openTasks > 0 || deleteTarget?.activeProcesses > 0) && (
              <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:12px;margin-bottom:16px;font-size:13px;line-height:1.7">
                <strong style="display:block;margin-bottom:4px">⚠ Aandachtspunten:</strong>
                {deleteTarget.openTasks > 0 && <div>• {deleteTarget.openTasks} openstaande {deleteTarget.openTasks === 1 ? 'taak wordt' : 'taken worden'} vrijgegeven</div>}
                {deleteTarget.activeProcesses > 0 && <div>• {deleteTarget.activeProcesses} actief {deleteTarget.activeProcesses === 1 ? 'proces wordt' : 'processen worden'} vrijgegeven</div>}
              </div>
            )}
            {deleteError && <div class="alert alert-error mb-3">{deleteError}</div>}
            <div class="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>{t('common.cancel')}</Button>
              <Button variant="danger" onClick={handleDeleteUser} disabled={deleting}>
                {deleting ? t('common.loading') : 'Permanent verwijderen'}
              </Button>
            </div>
          </Modal>
        </>
      ) : (
        /* Clubs tab — two-panel layout */
        <div style="display:grid;grid-template-columns:1fr 380px;gap:16px;align-items:start">
          {/* Club list */}
          <div class="card" style="padding:0;overflow-x:auto">
            <table style="width:100%;border-collapse:collapse">
              <thead>
                <tr style="border-bottom:2px solid var(--color-border)">
                  {[t('common.name'), t('common.type'), t('platform.tier'), t('platform.members'), t('platform.activeProcesses'), t('common.status')].map(h => (
                    <th key={h} style="padding:10px 14px;text-align:left;font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clubs.map(club => (
                  <tr
                    key={club.id}
                    style={`border-bottom:1px solid var(--color-border);cursor:pointer;background:${selectedClubId === club.id ? 'var(--color-primary-light,#eff6ff)' : 'transparent'}`}
                    onClick={() => setSelectedClubId(club.id === selectedClubId ? null : club.id)}
                  >
                    <td style="padding:10px 14px">
                      <div style="font-size:14px;font-weight:500">{club.name}</div>
                      <div style="font-size:11px;color:var(--color-text-muted)">{club.slug}</div>
                    </td>
                    <td style="padding:10px 14px;font-size:13px;color:var(--color-text-muted)">{club.type}</td>
                    <td style="padding:10px 14px"><TierBadge tier={club.subscription_tier || 'basis'} /></td>
                    <td style="padding:10px 14px;font-size:13px;text-align:center">{club.memberCount ?? 0}</td>
                    <td style="padding:10px 14px;font-size:13px;text-align:center">{club.activeProcesses ?? 0}</td>
                    <td style="padding:10px 14px">
                      <div style="display:flex;align-items:center;gap:6px">
                        <StatusDot active={club.is_active} />
                        <span style="font-size:12px;color:var(--color-text-muted)">{club.is_active ? t('common.active') : t('common.inactive')}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Club detail panel */}
          <div style="background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:10px;padding:16px;position:sticky;top:80px;max-height:calc(100vh - 120px);overflow-y:auto">
            <ClubDetailPanel
              clubId={selectedClubId}
              tiers={tiers}
              onUpdated={fetchAll}
            />
          </div>
        </div>
      )}

      {/* New club modal */}
      <Modal isOpen={showNew} title={t('platform.createClub.title')} onClose={() => setShowNew(false)}>
        {result ? (
          <div>
            <div class="alert alert-success mb-4">{t('platform.createClub.success')}</div>
            <div class="mb-3"><strong>{t('common.name')}:</strong> {result.club?.name}</div>
            <div class="mb-3"><strong>{t('common.type')}:</strong> {result.type}</div>
            <div class="mb-3"><strong>{t('platform.createClub.language')}:</strong> {result.language}</div>
            <div class="mb-3"><strong>{t('platform.createClub.tier')}:</strong> {tiers.find(tr => tr.value === result.club?.tier)?.label || result.club?.tier}</div>
            <div class="mb-3"><strong>{t('platform.createClub.templateCount')}:</strong> {result.templateCount}</div>
            <div class="mb-3"><strong>{t('platform.createClub.adminRole')}:</strong> {result.admin?.roleName}</div>
            {result.admin?.tempPassword && (
              <div class="alert alert-success mt-3">
                <strong>{t('platform.createClub.tempPassword')}:</strong>{' '}
                <code style="background:rgba(0,0,0,.08);padding:2px 6px;border-radius:4px">{result.admin.tempPassword}</code>
              </div>
            )}
            <div class="mt-4"><Button onClick={() => setShowNew(false)}>{t('common.close')}</Button></div>
          </div>
        ) : (
          <form onSubmit={createClub}>
            {error && <div class="alert alert-error mb-4">{error}</div>}
            <div class="form-group">
              <label class="form-label">{t('platform.createClub.clubName')}</label>
              <input class="form-input" value={form.name} onInput={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div class="form-group">
              <label class="form-label">{t('platform.createClub.slug')}</label>
              <input class="form-input" value={form.slug} onInput={e => setForm({ ...form, slug: e.target.value })} required />
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
              <div class="form-group">
                <label class="form-label">{t('platform.createClub.type')}</label>
                <select class="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  {clubTypes.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
                {selectedType && (
                  <div class="flex gap-1 mt-1">
                    {Object.values(selectedType.colors || {}).map((c, i) => (
                      <div key={i} style={`width:18px;height:18px;border-radius:4px;background:${c};border:1px solid var(--color-border)`} />
                    ))}
                  </div>
                )}
              </div>
              <div class="form-group">
                <label class="form-label">{t('platform.createClub.language')}</label>
                <select class="form-select" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                  {locales.map(l => (
                    <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">{t('platform.createClub.tier')}</label>
              <select class="form-select" value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })} required>
                <option value="" disabled>{t('platform.createClub.chooseTier')}</option>
                {tiers.map(tr => (
                  <option key={tr.value} value={tr.value}>{tr.label} — {tr.maxUsers ? `max. ${tr.maxUsers} gebruikers` : 'onbeperkt'}</option>
                ))}
                <option value="sportcomplex">Sportcomplex — eigen omgeving voor de beheerder van een complex</option>
              </select>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
              <div class="form-group">
                <label class="form-label">{t('platform.createClub.adminName')}</label>
                <input class="form-input" value={form.adminName} onInput={e => setForm({ ...form, adminName: e.target.value })} required />
              </div>
              <div class="form-group">
                <label class="form-label">{t('platform.createClub.adminEmail')}</label>
                <input class="form-input" type="email" value={form.adminEmail} onInput={e => setForm({ ...form, adminEmail: e.target.value })} required />
              </div>
            </div>
            <div class="flex gap-2 justify-between mt-2">
              <Button variant="secondary" type="button" onClick={() => setShowNew(false)}>{t('common.cancel')}</Button>
              <Button type="submit">{t('platform.createClub.create')}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
